import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { ArrowLeft, CheckCircle, XCircle, RefreshCw, AlertTriangle, LogOut } from 'lucide-react';

const COR = {
  laranja:  '#F07020',
  azul:     '#1E6DB5',
  ciano:    '#29B6D8',
  verde:    '#22C55E',
  amarelo:  '#F5A623',
  vermelho: '#EF4444',
  bg:       '#0A0F1A',
  card:     '#0E1826',
  card2:    '#121f30',
  borda:    '#1A2740',
  texto:    '#F1F5F9',
  sub:      '#64748B',
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${dt.getDate().toString().padStart(2,'0')}/${(dt.getMonth()+1).toString().padStart(2,'0')}/${dt.getFullYear()} ${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`;
};

export default function TelaDuplicidades() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [duplicidades, setDuplicidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('pendente');
  const [resolvendo, setResolvendo] = useState(null);
  const [expandido, setExpandido] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/registros/duplicidades?status=${filtroStatus}`);
      setDuplicidades(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  const resolver = async (id, decisao) => {
    setResolvendo(id + decisao);
    try {
      await api.post(`/registros/duplicidades/${id}/resolver`, { decisao });
      await carregar();
      setExpandido(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao resolver');
    }
    setResolvendo(null);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{minHeight:'100vh',background:COR.bg,padding:'20px 24px',fontFamily:'sans-serif'}}>
      <div style={{position:'fixed',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${COR.laranja},${COR.ciano},${COR.azul})`,zIndex:100}}/>

      {/* HEADER */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24,paddingTop:8}}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <button style={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'8px 12px',color:COR.sub,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:12}}
            onClick={() => navigate('/equipe')}>
            <ArrowLeft size={14}/> Voltar
          </button>
          <div style={{width:1,height:40,background:COR.borda}}/>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <AlertTriangle size={16} color={COR.amarelo}/>
              <h1 style={{color:COR.texto,fontSize:20,fontWeight:700,margin:0}}>Duplicidades Pendentes</h1>
              {duplicidades.filter(d => d.status === 'pendente').length > 0 && filtroStatus === 'pendente' && (
                <span style={{background:COR.vermelho,color:'#fff',borderRadius:12,padding:'2px 8px',fontSize:11,fontWeight:700}}>
                  {duplicidades.length}
                </span>
              )}
            </div>
            <p style={{color:COR.sub,fontSize:11,marginTop:2}}>Solicitações de validação dos operadores</p>
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button style={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'8px 10px',color:COR.sub,cursor:'pointer'}}
            onClick={carregar}>
            <RefreshCw size={14}/>
          </button>
          <button style={{display:'flex',alignItems:'center',gap:6,background:COR.card,border:'1px solid #ef444455',borderRadius:8,padding:'8px 14px',color:'#ef4444',cursor:'pointer',fontSize:12}}
            onClick={handleLogout}>
            <LogOut size={14}/> Sair
          </button>
        </div>
      </div>

      {/* FILTROS */}
      <div style={{display:'flex',gap:6,marginBottom:20}}>
        {['pendente','resolvido'].map(f => (
          <button key={f}
            style={{background:filtroStatus===f?`linear-gradient(135deg,${COR.laranja},#C85A10)`:COR.card,
              color:filtroStatus===f?'#fff':COR.sub,
              border:`1px solid ${filtroStatus===f?COR.laranja:COR.borda}`,
              borderRadius:8,padding:'7px 20px',cursor:'pointer',fontSize:13,fontWeight:500}}
            onClick={() => setFiltroStatus(f)}>
            {f.charAt(0).toUpperCase()+f.slice(1)}
          </button>
        ))}
      </div>

      {/* CONTEÚDO */}
      {loading ? (
        <div style={{display:'flex',justifyContent:'center',padding:'60px 0'}}>
          <div style={{width:32,height:32,border:`3px solid ${COR.borda}`,borderTop:`3px solid ${COR.laranja}`,borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
        </div>
      ) : duplicidades.length === 0 ? (
        <div style={{textAlign:'center',padding:'60px 0',color:COR.sub}}>
          <CheckCircle size={48} color={COR.verde} style={{marginBottom:12}}/>
          <p style={{fontSize:16,fontWeight:600,color:COR.texto,margin:'0 0 6px 0'}}>
            {filtroStatus === 'pendente' ? 'Nenhuma duplicidade pendente!' : 'Nenhuma duplicidade resolvida.'}
          </p>
          <p style={{fontSize:12,margin:0}}>
            {filtroStatus === 'pendente' ? 'Todos os registros estão em dia.' : 'Use o filtro "Pendente" para ver novas solicitações.'}
          </p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {duplicidades.map(dup => (
            <div key={dup.id} style={{background:COR.card,border:`1px solid ${dup.status==='pendente'?COR.amarelo+'44':COR.borda}`,borderRadius:12,overflow:'hidden'}}>
              {/* Cabeçalho do card */}
              <div style={{padding:'14px 18px',display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer',borderBottom:expandido===dup.id?`1px solid ${COR.borda}`:'none'}}
                onClick={() => setExpandido(expandido===dup.id ? null : dup.id)}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:dup.status==='pendente'?COR.amarelo:COR.verde,boxShadow:`0 0 8px ${dup.status==='pendente'?COR.amarelo:COR.verde}`}}/>
                  <div>
                    <p style={{color:COR.texto,fontSize:13,fontWeight:700,margin:0}}>{dup.razao_social}</p>
                    <p style={{color:COR.sub,fontSize:11,margin:'2px 0 0 0'}}>
                      CNPJ: {dup.cnpj} · Operador: <span style={{color:'#94a3b8'}}>{dup.operador_nome}</span> · {fmtDate(dup.criado_em)}
                    </p>
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{background:dup.tipo==='indicacao'?COR.amarelo+'22':COR.verde+'22',color:dup.tipo==='indicacao'?COR.amarelo:COR.verde,border:`1px solid ${dup.tipo==='indicacao'?COR.amarelo+'44':COR.verde+'44'}`,borderRadius:6,padding:'3px 10px',fontSize:11,fontWeight:700}}>
                    {dup.tipo.toUpperCase()}
                  </span>
                  {dup.status === 'resolvido' && (
                    <span style={{background:COR.verde+'22',color:COR.verde,border:`1px solid ${COR.verde+'44'}`,borderRadius:6,padding:'3px 10px',fontSize:11,fontWeight:700}}>
                      {dup.decisao === 'novo' ? '✓ Aprovado' : '✗ Rejeitado'}
                    </span>
                  )}
                  <span style={{color:COR.sub,fontSize:18}}>{expandido===dup.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Detalhes expandidos */}
              {expandido === dup.id && (
                <div style={{padding:'18px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
                    {/* Registro NOVO (solicitação do operador) */}
                    <div>
                      <p style={{color:COR.ciano,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,margin:'0 0 10px 0'}}>
                        📋 Solicitação do Operador
                      </p>
                      <div style={{background:'#080f1a',border:`1px solid ${COR.ciano}33`,borderRadius:10,padding:'12px 14px'}}>
                        <CampoInfo label="Razão Social" valor={dup.razao_social}/>
                        <CampoInfo label="CNPJ" valor={dup.cnpj}/>
                        <CampoInfo label="Cidade / Estado" valor={`${dup.cidade||'—'} / ${dup.estado||'—'}`}/>
                        <CampoInfo label="Contato" valor={dup.nome_completo}/>
                        <CampoInfo label="Telefone" valor={dup.telefone}/>
                        <CampoInfo label="E-mail" valor={dup.email}/>
                        <CampoInfo label="Ramo" valor={dup.ramo_atividade}/>
                        <CampoInfo label="Origem" valor={dup.origem}/>
                        <CampoInfo label="Operador" valor={dup.operador_nome}/>
                        <CampoInfo label="Equipe" valor={dup.equipe}/>
                      </div>
                    </div>

                    {/* Registro EXISTENTE no banco */}
                    <div>
                      <p style={{color:COR.amarelo,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,margin:'0 0 10px 0'}}>
                        🗃️ Registro Existente no Banco
                      </p>
                      <div style={{background:'#080f1a',border:`1px solid ${COR.amarelo}33`,borderRadius:10,padding:'12px 14px'}}>
                        {dup.registro_existente ? (
                          <>
                            <CampoInfo label="Razão Social" valor={dup.registro_existente.razao_social}/>
                            <CampoInfo label="CNPJ" valor={dup.registro_existente.cnpj}/>
                            <CampoInfo label="Cidade / Estado" valor={`${dup.registro_existente.cidade||'—'} / ${dup.registro_existente.estado||'—'}`}/>
                            <CampoInfo label="Contato" valor={dup.registro_existente.nome_completo}/>
                            <CampoInfo label="Telefone" valor={dup.registro_existente.telefone1}/>
                            <CampoInfo label="E-mail" valor={dup.registro_existente.email}/>
                            <CampoInfo label="Ramo" valor={dup.registro_existente.ramo_atividade}/>
                            <CampoInfo label="Origem" valor={dup.registro_existente.origem}/>
                            <CampoInfo label="Vendedor" valor={dup.registro_existente.vendedor}/>
                            <CampoInfo label="Data" valor={fmtDate(dup.registro_existente.carimbo_data_hora)}/>
                          </>
                        ) : (
                          <p style={{color:COR.sub,fontSize:12,fontStyle:'italic',margin:0}}>Registro original não encontrado.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Ações — só para pendente */}
                  {dup.status === 'pendente' && (
                    <div style={{display:'flex',gap:10,justifyContent:'flex-end',borderTop:`1px solid ${COR.borda}`,paddingTop:16}}>
                      <button
                        style={{display:'flex',alignItems:'center',gap:6,background:'#ef444415',border:'1px solid #ef444444',borderRadius:8,padding:'10px 20px',color:'#ef4444',cursor:'pointer',fontSize:13,fontWeight:600,opacity:resolvendo?0.7:1}}
                        onClick={() => resolver(dup.id, 'existente')}
                        disabled={!!resolvendo}>
                        <XCircle size={14}/>
                        Manter registro existente
                      </button>
                      <button
                        style={{display:'flex',alignItems:'center',gap:6,background:`${COR.verde}22`,border:`1px solid ${COR.verde}44`,borderRadius:8,padding:'10px 20px',color:COR.verde,cursor:'pointer',fontSize:13,fontWeight:600,opacity:resolvendo?0.7:1}}
                        onClick={() => resolver(dup.id, 'novo')}
                        disabled={!!resolvendo}>
                        <CheckCircle size={14}/>
                        Aprovar novo registro
                      </button>
                    </div>
                  )}

                  {dup.status === 'resolvido' && (
                    <div style={{borderTop:`1px solid ${COR.borda}`,paddingTop:12,color:COR.sub,fontSize:12}}>
                      Resolvido por <span style={{color:'#94a3b8',fontWeight:600}}>{dup.resolvido_por}</span> em {fmtDate(dup.resolvido_em)} —
                      Decisão: <span style={{color:dup.decisao==='novo'?COR.verde:'#ef4444',fontWeight:600}}>
                        {dup.decisao === 'novo' ? 'Novo registro aprovado' : 'Registro existente mantido'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CampoInfo({ label, valor }) {
  return (
    <div style={{marginBottom:6}}>
      <span style={{color:COR.sub,fontSize:10,display:'block'}}>{label}</span>
      <span style={{color:valor?COR.texto:'#334155',fontSize:12}}>{valor||'—'}</span>
    </div>
  );
}
